import { useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import GetGatheringForCoverPhotoSelectionDocument from "src/api/graphql/__generated__/documents/GetGatheringForCoverPhotoSelectionDocument";
import { parseGatheringThumbnailUrl } from "src/constants/GatheringThumbnail";
import useMountEffect from "src/hooks/useMountEffect";
import useStateRef from "src/hooks/useStateRef";
import useSyncRef from "src/hooks/useSyncRef";
import { EditCoverGatheringPhotoType } from "../types";

const key = "CoverPhotos";
const useEditCoverGatheringPhotos = (gatheringId: string) => {
	const { data, loading, refetch, fetchMore } = useQuery(
		GetGatheringForCoverPhotoSelectionDocument,
		{
			fetchPolicy: "cache-first",
			variables: {
				input: {
					gatheringId,
				},
				photosInput: {
					limit: 32,
					key,
				},
			},
		},
	);
	const photos = useMemo(() => {
		const parsedPhotos: EditCoverGatheringPhotoType[] = [];
		for (const photo of data?.getGathering?.photos?.payload ?? []) {
			const url = parseGatheringThumbnailUrl({ readUrl: photo.readUrl });
			if (!url) continue;
			parsedPhotos.push({
				ownerId: photo.ownerId,
				photoId: photo.photoId,
				url,
			});
		}
		return parsedPhotos;
	}, [data?.getGathering?.photos?.payload]);

	const cursorRef = useSyncRef(data?.getGathering?.photos?.cursor);
	const [isFetchingMore, setIsFetchingMore, isFetchingMoreRef] = useStateRef(false);
	const [isRefreshing, setIsRefreshing, isRefreshingRef] = useStateRef(false);
	const handleFetchMore = useCallback(async () => {
		if (isFetchingMoreRef.current) return;
		if (isRefreshingRef.current) return;
		if (cursorRef.current === "{}") return;
		try {
			setIsFetchingMore(true);
			await fetchMore({
				variables: {
					input: {
						gatheringId,
					},
					photosInput: {
						limit: 32,
						key,
						cursor: cursorRef.current,
					},
				},
			});
		} finally {
			setIsFetchingMore(false);
		}
	}, [cursorRef, fetchMore, gatheringId, isFetchingMoreRef, isRefreshingRef, setIsFetchingMore]);

	const handleRefresh = useCallback(async () => {
		if (isRefreshingRef.current) return;
		try {
			setIsRefreshing(true);
			await refetch({
				input: {
					gatheringId,
				},
				photosInput: {
					limit: 32,
					key,
				},
			});
		} finally {
			setIsRefreshing(false);
		}
	}, [gatheringId, isRefreshingRef, refetch, setIsRefreshing]);
	useMountEffect(() => {
		handleRefresh();
	}, []);

	return {
		photos,
		loading: loading || isFetchingMore,
		refresh: handleRefresh,
		refreshing: isRefreshing,
		fetchMore: handleFetchMore,
	};
};
export default useEditCoverGatheringPhotos;
